import {
  DataType,
  FormDialogMode,
  useNotify,
  UserPermission,
  SelectOption
} from '@globaleris/ui.library'
import { Box, Button, Menu, MenuItem, Portal, Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Sync } from '@material-ui/icons'
import { EnumCommodity, EnumTipoDocumento } from '../../api'
import { EnumEstadoLead } from '../../api/oData/enums/enumEstadoLead'
import {
  EvaluateLeadRequestModel,
  LeadAnnouncementResponseModel,
  LeadModel,
  LeadsSearchFilterModel
} from '../../api/Argentia/endpoints/leads/model/leads'
import { EnumDominioTarea } from '../../api/oData/enums/enumDominioTarea'
import { EnumTipoLead } from '../../api/oData/enums/enumTipoLead'
import Page from '../../components/page/page'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import PageHeader from '../../components/page/pageHeader'
import { CardWithTransition } from '../../components/shared/cardWithTransition/CardWithTransition'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import { ObjectionsDialog } from '../../components/shared/objectionsDialog/objectionsDialog'
import { ButtonOption } from '../../components/shared/splitButton/splitButton'
import {
  AttachFileDialog,
  AttachFileDialogRef
} from '../../components/shared/documents/attachFileDialog'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import {
  enumEnumToValueString,
  enumTranslateOption,
  enumValueToKey,
  enumToSelectOptionArrayDefaultValues
} from '../../helpers'
import { getUserPermissions, hasPermission } from '../../helpers/permissionsHelper'
import { fileDownloadHelper } from '../../helpers/fileDownloadHelper'
import { FormDialogDefinitionProps, useFormDialog, useHttpError, usePageState } from '../../hooks'
import { NonAuthRoutes } from '../../model'
import { Pages, Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  getActiveCommodityFilter,
  getLeadsSearchFilterState,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage,
  storeLeadsDataTable,
  storeLeadSearchModeState,
  storeLeadsSearchFilterState
} from '../../redux'
import { useLeadsDataTable, useLeadSearchMode } from '../../redux/hooks'
import { ObjectionTableModel } from '../invoicesPage/invoicesPage.Model'
import { fetchObjectionsDescriptions } from '../invoicesPage/invoicesPage.service'
import { publishQuoteMassiveAsync } from '../quotes/quotes.service'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../model/enums/informationDialogs'
import { downloadLeadPriceTemplate, uploadFile } from '../documents/documents.service'
import { mapDocumentsModelToUploadFileModel } from '../documents/documents.mapper'
import { mapAttachedDocumentFileItemToDocument } from '../procedures/procedures.mapper'
import { AttachedDocumentFile } from '../procedures/procedures.types'
import { LeadsKpiCard } from './components/leadsKpiCard/leadsKpiCard'
import { LeadTable } from './components/leadTable/leadTable'
import {
  LeadFormSearchFilter,
  SearchFilterCard
} from './components/searchFilterCard/searchFilterCard'
import {
  completeDataTableActions,
  mapLeadAnnoucementsToLeadTable,
  mapLeadFormSearchToFilterRequest,
  mapRowLeadModelToPublishQuoteMassiveRequest,
  mapRowLeadModelToRequest
} from './leads.mapper'
import {
  EnumLeadAnnouncementTypes,
  EvaluateLeadButtonOptions,
  EvaluateLeadModel,
  LeadsSearchMode
} from './leads.model'
import {
  evaluateLead,
  fetchLeads,
  getFilteredLeads,
  getLeadAnnouncements,
  getObjectionsByLeadId,
  deleteLeadById
} from './leads.service'

interface LeadsPageProps {
  location?: {
    state: {}
  }
}

export const LeadsPage: React.FC<LeadsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums', 'documents', 'confirmDialogs'])
  const history = useHistory()
  const { dataTableFilter, dataTableSearch, activeTab } = usePageState()
  const { openForm, closeForm, updateFormDefinition, CustomFormDialog, formProps } = useFormDialog()
  const { getConfirmation, closeConfirmation, getInformation, closeInformation } =
    useConfirmationDialog()
  const commodities = getActiveCommodities()
  const activeCommodityFilter = getActiveCommodityFilter()
  const leads = useLeadsDataTable()
  const searchMode = useLeadSearchMode()
  const storedFilters = getLeadsSearchFilterState()
  const { errorProps, showError, clearError } = useHttpError()
  const { error, info } = useNotify()

  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (activeCommodityFilter
      ? activeCommodityFilter
      : commodities !== undefined && commodities?.length > 0
        ? enumValueToKey(EnumCommodity, commodities[0])
        : EnumCommodity.GasNatural)

  const getInitialLeadType = () => {
    if (activeTab !== undefined) {
      // Map tab index to leadType: 0 -> Estandar(1), 1 -> Personalizado(2), 2 -> Precios(3)
      return activeTab + 1
    }
    return EnumTipoLead.Estandar
  }

  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [currentTab, setCurrentTab] = useState<number>(activeTab !== undefined ? activeTab : 0)
  const [leadType, setLeadType] = useState<EnumTipoLead>(getInitialLeadType())

  const [tableTitle, setTableTitle] = useState<string>()

  const [isFetchingLeadsTable, setIsFetchingLeadsTable] = useState<boolean>(false)
  const [showLeads, setShowLeads] = useState<boolean>(false)
  const [showSearchFilter, setShowSearchFilter] = useState<boolean>(false)
  const [filteredSearchIsClicked, setFilteredSearchIsClicked] = useState<boolean>(false)
  const [isFetchingAnnouncement, setIsFetchingAnnouncement] = useState<boolean>(false)
  const [announcements, setAnnouncements] = useState<LeadAnnouncementResponseModel[]>([])
  const [announcementType, setAnnouncementType] = useState<EnumLeadAnnouncementTypes>()
  const [isFetchingKpis, setIsFetchingKpis] = useState<boolean>(false)

  const [objections, setObjections] = useState<ObjectionTableModel[] | undefined>(undefined)
  const [objDescriptions, setObjDescriptions] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )
  const [showObjectionDialog, setShowObjectionDialog] = useState<boolean>(false)
  const [evaluateAnchorEl, setEvaluateAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedlead, setSelectedlead] = useState<LeadModel | null>(null)

  // Precios dialog state
  const attachFileDialogRef = useRef<AttachFileDialogRef>(null)
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<boolean>(false)
  const [attachFileDialogTitle, setAttachFileDialogTitle] = useState<string>('')
  const [documentsTypes, setDocumentsTypes] = useState<SelectOption[]>(
    enumToSelectOptionArrayDefaultValues(EnumTipoDocumento, 'enums:EnumTipoDocumento', [
      EnumTipoDocumento.LeadPriceTemplate
    ])
  )

  // #region Handlers Starts

  // ------------------- Tab & Commodity Handlers -----------------------------//

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)

      // set LeadTpe for current Tab
      const selectedLeadType = leadsTabsArray[tabIdx].value
      setLeadType(selectedLeadType)
    }
  }

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
    const resetFilters = {
      ...storedFilters,
      tollTypeId: [],
      productId: []
    }
    storeLeadsSearchFilterState(resetFilters)
  }

  // ------------------- SearchFilter Handlers -----------------------------//

  const onSearchCardHandler = () => {
    setFilteredSearchIsClicked(true)
    storeLeadSearchModeState(LeadsSearchMode.Filtered)
  }

  const onSearchFilterHandler = (formState: LeadFormSearchFilter) => {
    storeLeadsSearchFilterState(formState)
    const request = mapLeadFormSearchToFilterRequest(formState)
    fetchFilteredLeads(request)
  }

  const onCancelSearchHandler = () => {
    storeLeadSearchModeState(LeadsSearchMode.None)
    setShowSearchFilter(false)
    storeLeadsDataTable([])
    setTableTitle(undefined)
    onCleanFiltersHandler()
  }

  const onCleanFiltersHandler = () => {
    const emptyfilters: LeadFormSearchFilter = {
      code: '',
      commodity: commodity,
      leadType: leadType,
      description: '',
      productId: [],
      tollTypeId: []
    }
    storeLeadsSearchFilterState(emptyfilters)
  }

  const onPreciosClickHandler = () => {
    setAttachFileDialogTitle(t('leads:components.kpiCard.actions.prices'))
    setShowAttachmentDialog(true)
  }

  // ------------------- Precios Dialog Handlers -----------------------------//

  const handleClosePreciosDialog = () => {
    onResetDialog()
    setShowAttachmentDialog(false)
  }

  const handleSavePreciosDocument = async (document: AttachedDocumentFile, reopen?: boolean) => {
    await mapAttachedDocumentFileItemToDocument(document).then(async document => {
      const request = mapDocumentsModelToUploadFileModel([document], document.type)
      return await uploadFile(request)
        .then(response => {
          if (response.success) {
            info(t('leads:notifications.uploadFile.success'))
          } else {
            error(t('leads:notifications.uploadFile.error'))
          }
        })
        .finally(() => {})
    })
    handleClosePreciosDialog()
    reopen &&
      setTimeout(() => {
        setShowAttachmentDialog(true)
      }, 700)
  }

  const handleSaveAndNewPreciosDocument = (document: AttachedDocumentFile) => {
    handleSavePreciosDocument(document, true)
  }

  const onResetDialog = () => {
    attachFileDialogRef?.current && attachFileDialogRef?.current?.reset()
  }

  const handleDownloadPreciosTemplate = async () => {
    await downloadLeadPriceTemplate(commodity).then(response => {
      if (response.success && response.data) {
        fileDownloadHelper(response.data.name, response.data)
      } else {
        error(t('leads:fetchErrors.downloadTemplate'))
      }
    })
  }

  // ------------------- LatestCLick Handlers -----------------------------//

  const onLatestClickHandler = () => {
    storeLeadSearchModeState(LeadsSearchMode.Latest)
  }

  // ------------------- Add Lead Handlers -----------------------------//

  const handleAddLead = async (): Promise<boolean> => {
    history.push(NonAuthRoutes.LeadsAdd, {
      commodity: commodity,
      leadType: leadType
    })
    return true
  }

  // ------------------- DataTable Actions Handlers -----------------------------//

  const handleViewLeadFile = (rowSelected: LeadModel) => {
    history.push(NonAuthRoutes.LeadsFile, {
      leadId: rowSelected.id
    })
  }

  const handleEvaluateLead = async (rowSelected: LeadModel | LeadModel[]) => {
    const rows = Array.isArray(rowSelected) ? rowSelected : [rowSelected]
    const hasInvalidRows = rows.some(
      r =>
        r.leadState === EnumEstadoLead.EvaluacionSolicitada ||
        r.leadState === EnumEstadoLead.Procesando
    )
    if (hasInvalidRows) {
      await getInformation(InformationDialogs.LEAD_EVALUATE_MIXED_SELECTION).then(() =>
        closeInformation()
      )
      return
    }

    await getConfirmation(ConfirmationDialogs.EVALUATE_LEAD)
    evaluateLeadAsync(rows.map(mapRowLeadModelToRequest))
    closeConfirmation()
  }

  const handlePublishQuoteMassive = async (rowSelected: LeadModel | LeadModel[]) => {
    const rows = Array.isArray(rowSelected) ? rowSelected : [rowSelected]
    const hasInvalidRows = rows.some(r => r.leadState !== EnumEstadoLead.Evaluado)
    if (hasInvalidRows) {
      await getInformation(InformationDialogs.LEAD_PUBLISH_MIXED_SELECTION).then(() =>
        closeInformation()
      )
      return
    }

    await getConfirmation(ConfirmationDialogs.PUBLISH_LEAD)

    const requests = rows.map(mapRowLeadModelToPublishQuoteMassiveRequest)

    setIsFetchingLeadsTable(true)
    await publishQuoteMassiveAsync(requests).then(async response => {
      if (response.success) {
        info(t('leads:notifications.publishQuote.success'))
        await refreshLeadsPage()
      } else {
        error(t('leads:notifications.publishQuote.error'))
        setIsFetchingLeadsTable(false)
      }
    })
    closeConfirmation()
  }

  const handleViewLeadObjections = async (rowSelected: LeadModel) => {
    setShowObjectionDialog(true)
    await getObjectionsByLeadId(rowSelected.id).then(response => {
      if (response.success && response.data) {
        setObjections(response.data)
      } else {
        error(t('leads:notifications.getObjectionsError'))
      }
    })
  }

  const handleDeleteLead = async (rowSelected: LeadModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_LEAD)
    await deleteLeadById(rowSelected.id).then(async response => {
      if (response.success) {
        info(t('leads:notifications.deleteLead.success'))
        await refreshLeadsPage()
      } else {
        error(t('leads:notifications.deleteLead.error'))
      }
    })
    closeConfirmation()
  }

  // ------------------- Announcement Actions Handlers -----------------------------//
  const handleAnnouncementView = (annoucementTypeParam: EnumLeadAnnouncementTypes) => {
    storeLeadSearchModeState(LeadsSearchMode.AnnouncementList)
    setAnnouncementType(annoucementTypeParam)
  }

  // ------------------- Objections Actions Handlers -----------------------------//
  const handleObjectionsClose = () => {
    setShowObjectionDialog(false)
    setObjections(undefined)
  }

  // --------------------------- Evaluate Menu Handlers -----------------------------------//

  const onEvaluateCloseHandler = useCallback(() => {
    setEvaluateAnchorEl(null)
  }, [])

  const handleEvaluateCustomLead = (row: LeadModel, event?: any) => {
    setSelectedlead(row)
    setEvaluateAnchorEl(event.currentTarget)
  }

  const evaluateLeadAsync = async (request: EvaluateLeadRequestModel[]) => {
    setIsFetchingLeadsTable(true)
    await evaluateLead(request).then(async response => {
      if (response.success) {
        info(t('leads:notifications.evaluateLead.success'))
        await refreshLeadsPage()
      } else {
        error(t('leads:notifications.evaluateLead.error'))
        setIsFetchingLeadsTable(false)
      }
    })
  }

  const onEvaluateClickHandler = useCallback(
    async (option: ButtonOption) => {
      if (selectedlead) {
        let leadRequest: EvaluateLeadModel = { id: selectedlead?.id } as EvaluateLeadModel
        switch (option.id) {
          case EvaluateLeadButtonOptions.EVALUATE_LEAD:
            leadRequest.startDate = null
            leadRequest.endDate = null
            evaluateLeadAsync([mapRowLeadModelToRequest(leadRequest)])
            break
          case EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE:
            openForm({
              title: t('leads:forms.evaluateLeadToDateForm.title'),
              definition: evaluateLeadToDateFormDef,
              onClose: handleEvaluateLeadToDateClose,
              mode: FormDialogMode.ADD
            })
            break
          default:
            break
        }
      }
      onEvaluateCloseHandler()
    },
    [selectedlead, onEvaluateCloseHandler]
  )

  const handleEvaluateLeadToDateClose = useCallback(
    (formData?: any) => {
      if (formData) {
        const leadRequest: EvaluateLeadModel = {
          id: selectedlead?.id,
          startDate: formData.startDate,
          endDate: formData.endDate
        } as EvaluateLeadModel
        evaluateLeadAsync([mapRowLeadModelToRequest(leadRequest)])
      }
      closeForm()
    },
    [selectedlead, closeForm, evaluateLeadAsync]
  )

  // #endregion Handlers Ends

  // #region Functions Starts
  const filterLeadsTableByAnnouncementType = useCallback(() => {
    switch (announcementType) {
      case EnumLeadAnnouncementTypes.HAS_DRAFT_QUOTE:
        storeLeadsDataTable(
          mapLeadAnnoucementsToLeadTable(announcements.filter(item => item.hasDraftQuote))
        )
        setTableTitle(t('leads:components.leadTable.table.title.hasDraftQuote'))
        break
      case EnumLeadAnnouncementTypes.HAS_NOT_CURRENT_QUOTES:
        storeLeadsDataTable(
          mapLeadAnnoucementsToLeadTable(announcements.filter(item => item.hasNotCurrentQuotes))
        )
        setTableTitle(t('leads:components.leadTable.table.title.hasNotCurrentQuote'))
        break
      case EnumLeadAnnouncementTypes.HAS_REJECTED_QUOTES:
        storeLeadsDataTable(
          mapLeadAnnoucementsToLeadTable(announcements.filter(item => item.hasRejectedQuotes))
        )
        setTableTitle(t('leads:components.leadTable.table.title.hasRejectedQuote'))
        break
      default:
        break
    }
  }, [announcementType, announcements])

  // #endregion Functions Ends

  // #region Form Definition Starts
  const evaluateLeadToDateFormDef: FormDialogDefinitionProps = useMemo(() => {
    return {
      id: 'evaluateLeadToDateForm',
      sections: [
        {
          inputs: [
            {
              name: 'startDate',
              label: t('leads:forms.evaluateLeadToDateForm.inputs.startDate'),
              type: DataType.DATE
            },
            {
              name: 'endDate',
              label: t('leads:forms.evaluateLeadToDateForm.inputs.endDate'),
              type: DataType.DATE
            }
          ]
        }
      ]
    }
  }, [])

  useEffect(() => {
    updateFormDefinition(evaluateLeadToDateFormDef)
  }, [evaluateLeadToDateFormDef])
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
      const selectedLeadType = leadsTabsArray[activeTab].value
      setLeadType(selectedLeadType)

      if (searchMode === LeadsSearchMode.None) {
        storeLeadSearchModeState(LeadsSearchMode.Latest)
      }
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  const refreshLeadsPage = async () => {
    switch (searchMode) {
      case LeadsSearchMode.Latest:
        getLeads(commodity, leadType)
        break
      case LeadsSearchMode.Filtered:
        if (filteredSearchIsClicked) {
          const request = mapLeadFormSearchToFilterRequest(storedFilters)
          fetchFilteredLeads(request)
        }
        break
      case LeadsSearchMode.AnnouncementList:
        await getAnnouncements().then(response => {
          if (response && announcementType !== EnumLeadAnnouncementTypes.GENERIC_EMPTY) {
            filterLeadsTableByAnnouncementType()
          }
        })
        break
      default:
        break
    }
  }

  const getLeads = useCallback(async (commodity: EnumCommodity, leadType: EnumTipoLead) => {
    setIsFetchingLeadsTable(true)
    clearError()
    await fetchLeads(commodity, leadType)
      .then(response => {
        if (response.success && response.data) {
          storeLeadsDataTable(completeDataTableActions(response.data))
        } else {
          showError({
            errorCode: response.error?.code,
            onRefresh: () => getLeads(commodity, leadType),
            title: response.error?.message
          })
        }
      })
      .finally(() => {
        setIsFetchingLeadsTable(false)
      })
  }, [])

  const getAnnouncements = useCallback(async () => {
    setIsFetchingAnnouncement(true)
    let success: boolean = false
    await getLeadAnnouncements(commodity, leadType)
      .then(response => {
        if (response.success && response.data) {
          setAnnouncements(
            completeDataTableActions(response.data) as LeadAnnouncementResponseModel[]
          )
          success = true
        } else {
          error(t('leads:notifications.getLeadAnnouncements.error'))
        }
      })
      .finally(() => setIsFetchingAnnouncement(false))
    return success
  }, [commodity, leadType])

  const fetchFilteredLeads = async (request: LeadsSearchFilterModel) => {
    setIsFetchingLeadsTable(true)
    await getFilteredLeads(request)
      .then(response => {
        if (response.success && response.data) {
          storeLeadsDataTable(completeDataTableActions(response.data))
        } else {
          error(t('leads:notifications.getFilteredLeads.error'))
        }
      })
      .finally(() => setIsFetchingLeadsTable(false))
  }

  useEffect(() => {
    if (leadType != EnumTipoLead.Precios) {
      getAnnouncements()
    } else {
      setAnnouncements([])
    }
  }, [commodity, leadType, getAnnouncements])

  useEffect(() => {
    switch (searchMode) {
      case LeadsSearchMode.Filtered:
        setShowSearchFilter(true)
        setShowLeads(true)
        setTableTitle(t('leads:components.leadTable.table.title.search'))
        if (!filteredSearchIsClicked) {
          const request = mapLeadFormSearchToFilterRequest(storedFilters)
          fetchFilteredLeads(request)
          setShowLeads(true)
        } else {
          storeLeadsDataTable([])
        }
        break
      case LeadsSearchMode.Latest:
        setShowLeads(true)
        setShowSearchFilter(false)
        setTableTitle(
          t('leads:components.leadTable.table.title.latest', {
            leadType: enumTranslateOption(leadType, EnumTipoLead, 'enums:EnumTipoLead')
          })
        )
        getLeads(commodity, leadType)
        break
      case LeadsSearchMode.AnnouncementList:
        setShowSearchFilter(false)
        setShowLeads(true)
        filterLeadsTableByAnnouncementType()
        break
      default:
        break
    }
  }, [
    searchMode,
    commodity,
    leadType,
    showLeads,
    showSearchFilter,
    announcementType,
    filteredSearchIsClicked,
    announcements,
    filterLeadsTableByAnnouncementType
  ])

  useEffect(() => {
    const getObjectionsDescriptions = async () => {
      await fetchObjectionsDescriptions(EnumDominioTarea.Lead).then(response => {
        setObjDescriptions(response.data)
      })
    }
    getObjectionsDescriptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion Hooks Ends

  // #region Tab Config Starts
  const leadsTabsArray = [
    {
      name: enumEnumToValueString(EnumTipoLead, EnumTipoLead.Estandar),
      value: EnumTipoLead.Estandar
    },
    {
      name: enumEnumToValueString(EnumTipoLead, EnumTipoLead.Personalizado),
      value: EnumTipoLead.Personalizado
    },
    {
      name: enumEnumToValueString(EnumTipoLead, EnumTipoLead.Precios),
      value: EnumTipoLead.Precios
    }
  ]
  const tabsDefinition: Map<number, Tab> = new Map(
    leadsTabsArray.map((leadType, tabIndex) => [
      tabIndex,
      {
        id: leadType.value,
        label: t(`leads:landingPage.tabs.${leadType.name}`),
        hasPermission: hasPermission(Resources.Leads, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ])
  )
  const filteredTabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  // #endregion Tab Config Ends

  // #region Render Functions Starts
  const renderTabContent = (activeTab: number) => {
    return (
      filteredTabsArray[activeTab]?.id && (
        <LeadsKpiCard
          leadType={filteredTabsArray[activeTab]?.id}
          isFetchingAnnouncements={isFetchingAnnouncement}
          announcementsList={announcements}
          onSearchClickHandler={onSearchCardHandler}
          onPreciosClickHandler={onPreciosClickHandler}
          onLatestClickHandler={onLatestClickHandler}
          onAddLead={handleAddLead}
          onAnnouncementViewClick={handleAnnouncementView}
        />
      )
    )
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!(isFetchingAnnouncement || isFetchingKpis)}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }
  // #endregion Render Functions Ends

  // Menu Options Definition
  const evaluateOptions: ButtonOption[] = [
    {
      id: EvaluateLeadButtonOptions.EVALUATE_LEAD,
      label: t('leads:menu.evaluateLead.option1'),
      name: Object.keys(EvaluateLeadButtonOptions.EVALUATE_LEAD)[0]
    },
    {
      id: EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE,
      label: t('leads:menu.evaluateLead.option2'),
      name: Object.keys(EvaluateLeadButtonOptions.EVALUATE_LEAD)[1]
    }
  ]

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('leads:landingPage.title')}>
        <Box display="flex" justifyContent="flex-end">
          {renderCommodities()}
        </Box>
      </PageHeader>
      <Box display="flex">
        <PageCenterColumn>
          <TabsWithPermissions
            tabs={tabsDefinition}
            tabDefault={currentTab}
            onTabChange={onTabChangeHandler}
          />
          {renderTabContent(activeTab ? activeTab : currentTab)}
          <SearchFilterCard
            showCard={showSearchFilter}
            filters={storedFilters}
            onSearch={onSearchFilterHandler}
            onCancel={onCancelSearchHandler}
            onCleanFilters={onCleanFiltersHandler}
            commodity={commodity}
            leadType={leadType}
          />
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Button>
              <Sync onClick={refreshLeadsPage} />
            </Button>
          </Box>
          <CardWithTransition show={showLeads}>
            <LeadTable
              title={tableTitle}
              isLoading={isFetchingLeadsTable}
              data={leads}
              onTableAdd={handleAddLead}
              onRowEvaluateLead={
                leadType === EnumTipoLead.Estandar ? handleEvaluateLead : undefined
              }
              onRowEvaluateCustomLead={
                leadType === EnumTipoLead.Personalizado ? handleEvaluateCustomLead : undefined
              }
              onRowPublishQuoteMassive={
                leadType !== EnumTipoLead.Precios ? handlePublishQuoteMassive : undefined
              }
              onRowViewLeadFile={handleViewLeadFile}
              onRowViewObjections={handleViewLeadObjections}
              onRowDeleteLead={handleDeleteLead}
              dataTableFilter={dataTableFilter}
              dataTableSearch={dataTableSearch}
              dataTableErrorProps={errorProps}
              disableSelectableRows={leadType === EnumTipoLead.Precios}
              commodity={commodity}
            />
          </CardWithTransition>

          <ObjectionsDialog
            open={showObjectionDialog}
            handleClose={handleObjectionsClose}
            title={t('leads:dialogs.objections.title')}
            objDescriptions={objDescriptions ?? []}
            objectionsData={objections}
            tableUserPermissions={getUserPermissions(Pages.Leads)}
            errorProps={errorProps}
          />

          <AttachFileDialog
            prolifeFormConfig
            ref={attachFileDialogRef}
            open={showAttachmentDialog}
            documentTypes={documentsTypes}
            onClose={handleClosePreciosDialog}
            onSave={handleSavePreciosDocument}
            onSaveAndNew={handleSaveAndNewPreciosDocument}
            ondownloadTemplate={handleDownloadPreciosTemplate}
            customTitle={attachFileDialogTitle}
          />
        </PageCenterColumn>
      </Box>
      <Portal>
        <Menu
          keepMounted={false}
          elevation={2}
          anchorEl={evaluateAnchorEl}
          open={Boolean(evaluateAnchorEl)}
          onClose={onEvaluateCloseHandler}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          {evaluateOptions.map((option, index) => {
            const disabled =
              leadType !== EnumTipoLead.Personalizado &&
              option.id === EvaluateLeadButtonOptions.EVALUATE_WITHIN_DATE
            return (
              <MenuItem
                disabled={disabled}
                dense
                key={index}
                onClick={() => onEvaluateClickHandler(option)}
              >
                <Typography variant="inherit">{option.label}</Typography>
              </MenuItem>
            )
          })}
        </Menu>
      </Portal>
      <CustomFormDialog {...formProps} />
    </Page>
  )
}
